// Mixpanel analytics - init and track helpers
// Token from env (NEXT_PUBLIC_MIXPANEL_TOKEN) per global rules
// If GTM or another script already loaded Mixpanel (window.mixpanel), we use that to avoid double-load errors.
// mixpanel-browser se učitava lijeno (dynamic import) da ne usporava prvi load stranice;
// pozivi prije učitavanja se čuvaju u redu i izvrše kad se biblioteka učita.

import type { Mixpanel } from "mixpanel-browser";

const token = process.env.NEXT_PUBLIC_MIXPANEL_TOKEN;

let mpInstance: Mixpanel | null = null;
let loadPromise: Promise<Mixpanel | null> | null = null;
let pending: ((mp: Mixpanel) => void)[] = [];

declare global {
  interface Window {
    mixpanel?: { init: (t: string, o?: object) => void; track: (e: string, p?: object) => void; identify: (id: string) => void; people: { set: (p: object) => void }; reset: () => void };
  }
}

function getGlobalMixpanel(): Mixpanel | null {
  if (typeof window === "undefined") return null;
  const w = window as Window & { mixpanel?: Mixpanel };
  if (w.mixpanel && typeof w.mixpanel.track === "function") return w.mixpanel;
  return null;
}

function loadMixpanel(): Promise<Mixpanel | null> {
  if (loadPromise) return loadPromise;

  loadPromise = (async () => {
    const globalMp = getGlobalMixpanel();
    if (globalMp) return globalMp;

    const { default: mixpanel } = await import("mixpanel-browser");
    mixpanel.init(token!, {
      debug: process.env.NODE_ENV === "development",
      track_pageview: true,
      persistence: "localStorage",
      autocapture: true,
      record_sessions_percent: 100,
    });
    return mixpanel;
  })()
    .then((mp) => {
      mpInstance = mp;
      if (mp) pending.forEach((fn) => fn(mp));
      pending = [];
      return mp;
    })
    .catch((error) => {
      if (process.env.NODE_ENV === "development") {
        console.error("[mixpanel] Failed to load", error);
      }
      pending = [];
      return null;
    });

  return loadPromise;
}

/** Pokreće učitavanje Mixpanela kad je browser slobodan (ne blokira prvi render). */
export function initMixpanel(): void {
  if (typeof window === "undefined" || !token) return;
  if (loadPromise) return;

  const start = () => void loadMixpanel();
  if ("requestIdleCallback" in window) {
    window.requestIdleCallback(start, { timeout: 4000 });
  } else {
    setTimeout(start, 2000);
  }
}

function withMixpanel(fn: (mp: Mixpanel) => void): void {
  if (typeof window === "undefined" || !token) return;
  if (mpInstance) {
    fn(mpInstance);
    return;
  }
  pending.push(fn);
  initMixpanel();
}

export function mixpanelIdentify(userId: string, props?: { $name?: string; $email?: string; [key: string]: unknown }): void {
  withMixpanel((mp) => {
    mp.identify(userId);
    if (props && Object.keys(props).length > 0) {
      mp.people.set(props);
    }
  });
}

export function mixpanelTrack(eventName: string, properties?: Record<string, unknown>): void {
  withMixpanel((mp) => mp.track(eventName, properties));
}

export function mixpanelReset(): void {
  withMixpanel((mp) => mp.reset());
}
